import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Tabs, Tab, Avatar } from '@heroui/react';
import { AlertBlock, ResponsiveTooltip, Typography } from '../../components';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslations } from '../../contexts/translation.context';
import { PartnerNewsBlock } from './PartnerNewsBlock';
import { partnerAuthService } from '../../api';
import { SimpleStatCard } from '../../client/components/SimpleStatCard';
import { IcTgBlue, LvlCheck } from '../../components/icons';
import AvatarTest from './avatarnew.jpg';
import { Link } from 'react-router';
import { useIsMobile } from '../../hooks/useMobile';

// Значения параметра company_type в /partners/dashboard/*
const COMPANY_TYPE_CPL = 'CPL';
const COMPANY_TYPE_REFSHARE = 'RefShare';
const COMPANY_TYPE_SUBSCRIPTION = 'Subscription';

interface IncomeData {
  all_time: string;
  week_ago: string;
  month_ago: string;
}

interface SubscribersData {
  all_time: number;
  week_ago: number;
  month_ago: number;
}

interface ConversionsData {
  gte_3_messages_count: number;
  sales: number;
  arpu: string;
}

interface LevelRequirements {
  base: number;
  pro: number;
  expert: number;
}

interface LevelStats {
  total_cost: string;
  total_subscribers_count: number;
  last_completed_date: string | null;
  last_in_progress_date: string | null;
  last_waiting_date: string | null;
  last_canceled_date: string | null;
}

const ApprovedPartnerDashboard: React.FC = () => {
  const isMobile = useIsMobile();
  const { user, partnerOrdersLength } = useAuth();
  const { t } = useTranslations();
  const managerName = t('dashboard.managerName');
  const managerTelegramLink = t('dashboard.managerTelegramLink');
  const managerAvatarAlt = t('dashboard.managerAvatarAlt');
  const [activeIncomeTab, setActiveIncomeTab] = useState('all');
  const [activeFansTab, setActiveFansTab] = useState('all');
  const [activeConversionTab, setActiveConversionTab] = useState('all');

  const [incomeData, setIncomeData] = useState<IncomeData>({
    all_time: '0',
    week_ago: '0',
    month_ago: '0',
  });
  const [subscribersData, setSubscribersData] = useState<SubscribersData>({
    all_time: 0,
    week_ago: 0,
    month_ago: 0,
  });
  const [conversionsData, setConversionsData] = useState<ConversionsData>({
    gte_3_messages_count: 0,
    sales: 0,
    arpu: '0',
  });
  const [levelRequirements, setLevelRequirements] = useState<LevelRequirements>({
    base: 200,
    pro: 500,
    expert: 2000,
  });
  const [levelStats, setLevelStats] = useState<LevelStats>({
    total_cost: '0',
    total_subscribers_count: 0,
    last_completed_date: null,
    last_in_progress_date: null,
    last_waiting_date: null,
    last_canceled_date: null,
  });
  const [totalMonthSubscribers, setTotalMonthSubscribers] = useState(0);

  // Partner levels progression
  const levels = ['new', 'base', 'pro', 'expert'];
  const currentLevel = user?.lvl || 'new';
  const currentLevelIndex = levels.indexOf(currentLevel.toLowerCase());

  const getIncomeData = async (companyType?: string) => {
    try {
      if (companyType === 'all' || !companyType) {
        // Делаем запросы по всем типам кампаний и суммируем
        const [resCPL, resRefShare, resSubscription] = await Promise.all([
          partnerAuthService.getDashboardDataIncome({ company_type: COMPANY_TYPE_CPL }),
          partnerAuthService.getDashboardDataIncome({ company_type: COMPANY_TYPE_REFSHARE }),
          partnerAuthService.getDashboardDataIncome({ company_type: COMPANY_TYPE_SUBSCRIPTION }),
        ]);

        if (resCPL?.data && resRefShare?.data) {
          // Суммируем результаты
          const summedData: IncomeData = {
            all_time: (
              parseFloat(resCPL.data.all_time || '0') +
              parseFloat(resRefShare.data.all_time || '0') +
              parseFloat(resSubscription?.data?.all_time || '0')
            ).toString(),
            week_ago: (
              parseFloat(resCPL.data.week_ago || '0') +
              parseFloat(resRefShare.data.week_ago || '0') +
              parseFloat(resSubscription?.data?.week_ago || '0')
            ).toString(),
            month_ago: (
              parseFloat(resCPL.data.month_ago || '0') +
              parseFloat(resRefShare.data.month_ago || '0') +
              parseFloat(resSubscription?.data?.month_ago || '0')
            ).toString(),
          };
          setIncomeData(summedData);
        }
      } else {
        const params = { company_type: companyType };
        const resIncome = await partnerAuthService.getDashboardDataIncome(params);
        if (resIncome?.data) setIncomeData(resIncome.data);
      }
    } catch (err: any) {
      console.log('err', err);
    }
  };

  const getSubscribersData = async (companyType?: string) => {
    try {
      if (companyType === 'all' || !companyType) {
        // Делаем запросы по всем типам кампаний и суммируем
        const [resCPL, resRefShare, resSubscription] = await Promise.all([
          partnerAuthService.getDashboardDataSubscribers({ company_type: COMPANY_TYPE_CPL }),
          partnerAuthService.getDashboardDataSubscribers({ company_type: COMPANY_TYPE_REFSHARE }),
          partnerAuthService.getDashboardDataSubscribers({
            company_type: COMPANY_TYPE_SUBSCRIPTION,
          }),
        ]);

        if (resCPL?.data && resRefShare?.data) {
          // Суммируем результаты
          const summedData: SubscribersData = {
            all_time:
              (resCPL.data.all_time || 0) +
              (resRefShare.data.all_time || 0) +
              (resSubscription?.data?.all_time || 0),
            week_ago:
              (resCPL.data.week_ago || 0) +
              (resRefShare.data.week_ago || 0) +
              (resSubscription?.data?.week_ago || 0),
            month_ago:
              (resCPL.data.month_ago || 0) +
              (resRefShare.data.month_ago || 0) +
              (resSubscription?.data?.month_ago || 0),
          };
          setSubscribersData(summedData);
          setTotalMonthSubscribers(summedData.month_ago);
        }
      } else {
        const params = { company_type: companyType };
        const resSubscribers = await partnerAuthService.getDashboardDataSubscribers(params);
        if (resSubscribers?.data) setSubscribersData(resSubscribers.data);
      }
    } catch (err: any) {
      console.log('err', err);
    }
  };

  const getConversionsData = async (companyType?: string) => {
    try {
      if (companyType === 'all' || !companyType) {
        // Делаем запросы по всем типам кампаний и суммируем
        const [resCPL, resRefShare, resSubscription] = await Promise.all([
          partnerAuthService.getDashboardDataConversions({ company_type: COMPANY_TYPE_CPL }),
          partnerAuthService.getDashboardDataConversions({ company_type: COMPANY_TYPE_REFSHARE }),
          partnerAuthService.getDashboardDataConversions({
            company_type: COMPANY_TYPE_SUBSCRIPTION,
          }),
        ]);

        if (resCPL?.data && resRefShare?.data) {
          // Суммируем результаты
          const summedData: ConversionsData = {
            gte_3_messages_count:
              (resCPL.data.gte_3_messages_count || 0) +
              (resRefShare.data.gte_3_messages_count || 0) +
              (resSubscription?.data?.gte_3_messages_count || 0),
            sales:
              (resCPL.data.sales || 0) +
              (resRefShare.data.sales || 0) +
              (resSubscription?.data?.sales || 0),
            arpu: (
              parseFloat(resCPL.data.arpu || '0') +
              parseFloat(resRefShare.data.arpu || '0') +
              parseFloat(resSubscription?.data?.arpu || '0')
            ).toString(),
          };
          setConversionsData(summedData);
        }
      } else {
        const params = { company_type: companyType };
        const resConversions = await partnerAuthService.getDashboardDataConversions(params);
        if (resConversions?.data) setConversionsData(resConversions.data);
      }
    } catch (err: any) {
      console.log('err', err);
    }
  };

  const getLevelRequirementsData = async () => {
    try {
      const res = await partnerAuthService.getLevelRequirements();
      if (res?.data) setLevelRequirements(res.data);
    } catch (err: any) {
      console.log('err', err);
    }
  };

  const getLevelStatsData = async () => {
    try {
      const res = await partnerAuthService.getLevelStats();
      if (res?.data) setLevelStats(res.data);
    } catch (err: any) {
      console.log('err', err);
    }
  };

  const formatMoney = (value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  // Расчет прогресса для следующего уровня
  const getNextLevelInfo = () => {
    const nextLevelIndex = currentLevelIndex + 1;
    if (nextLevelIndex >= levels.length) {
      return { nextLevel: 'expert', targetSubscribers: levelRequirements.expert, progress: 100 };
    }

    const nextLevel = levels[nextLevelIndex];
    const targetSubscribers = levelRequirements[nextLevel as keyof LevelRequirements] || 0;
    const currentSubscribers = totalMonthSubscribers;
    const progress =
      targetSubscribers > 0 ? Math.min((currentSubscribers / targetSubscribers) * 100, 100) : 0;

    return { nextLevel, targetSubscribers, progress };
  };

  const nextLevelInfo = getNextLevelInfo();

  // "В ожидании RevShare" показываем только там, где это осмысленно —
  // у CPL и подписки такой строки нет.
  const isCplIncomeTab =
    activeIncomeTab?.toLowerCase?.() === 'cpl' ||
    activeIncomeTab?.toLowerCase?.() === COMPANY_TYPE_SUBSCRIPTION.toLowerCase();

  useEffect(() => {
    getIncomeData();
    getSubscribersData();
    getConversionsData();
    getLevelRequirementsData();
    getLevelStatsData();
  }, []);

  useEffect(() => {
    const companyType = activeIncomeTab === 'all' ? undefined : activeIncomeTab;
    getIncomeData(companyType);
  }, [activeIncomeTab]);

  useEffect(() => {
    const companyType = activeFansTab === 'all' ? undefined : activeFansTab;
    getSubscribersData(companyType);
  }, [activeFansTab]);

  useEffect(() => {
    const companyType = activeConversionTab === 'all' ? undefined : activeConversionTab;
    getConversionsData(companyType);
  }, [activeConversionTab]);
  return (
    <div className="flex flex-col gap-6 md:gap-4 p-4 py-6 md:px-6 md:py-4">
      {/* Header */}
      <div className="md:mb-2">
        <h1 className="text-xl md:text-2xl font-medium md:font-semibold text-auth-ink">
          {t('dashboard.title')}
        </h1>
      </div>
      {partnerOrdersLength?.length > 0 && (
        <div className="mb-6">
          <AlertBlock
            data={[
              {
                severity: 'info',
                title: t('dashboard.firstStep'),
                description: t('dashboard.firstStepDescription'),
              },
            ]}
          />
        </div>
      )}
      {/* Top Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:mb-6">
        {/* Partner Level Card */}
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-gray-90 rounded-3.5 flex flex-col shadow-[0_4px_16px_0_rgba(0,0,0,0.03)]">
            {/* Title and Progress Bar */}
            <div className="px-4 py-3 h-[5.5rem] flex flex-col gap-2 justify-center">
              <Typography variant="h5" className="!font-medium !text-xl">
                {t('dashboard.yourLevel')} - {currentLevel.toUpperCase()}
              </Typography>
              <div className="flex flex-col gap-1">
                <ResponsiveTooltip content={t('dashboard.levelTooltip')} placement="top">
                  <div className="w-full bg-tin rounded-2 h-2 overflow-hidden">
                    <div
                      className="bg-partner h-full rounded-2"
                      style={{ width: `${nextLevelInfo.progress}%` }}
                    />
                  </div>
                </ResponsiveTooltip>
                <div className="flex gap-1 items-center">
                  <Typography variant="body4" className="!text-auth-muted !text-xxs">
                    {t('dashboard.progressToLevel', {
                      level:
                        nextLevelInfo.nextLevel.charAt(0).toUpperCase() +
                        nextLevelInfo.nextLevel.slice(1),
                    })}
                    :
                  </Typography>
                  <Typography variant="label4" className="!text-auth-ink !text-xxs !font-medium">
                    {Math.round(nextLevelInfo.progress)}%
                  </Typography>
                </div>
              </div>
            </div>

            {/* Partner Progress with Chips */}
            <div className="px-4 py-4 h-[5.5rem] flex items-center justify-center border-y border-gray-90">
              <div className="flex items-center gap-2 w-full">
                {levels.map((level, index) => (
                  <React.Fragment key={level}>
                    {index > 0 && (
                      <div
                        className={`flex-1 h-[0.1875rem] rounded-full ${index <= currentLevelIndex ? 'bg-partner' : 'bg-neutral-12'}`}
                      ></div>
                    )}
                    {index < currentLevelIndex ? (
                      // Completed level - blue circle with checkmark
                      <ResponsiveTooltip content={t('dashboard.levelName', { level: level.charAt(0).toUpperCase() + level.slice(1) })} placement="top">
                        <div className="bg-partner rounded-full w-8 h-8 flex items-center justify-center">
                          <LvlCheck />
                        </div>
                      </ResponsiveTooltip>
                    ) : index === currentLevelIndex ? (
                      // Current level - outlined with label
                      <div className="bg-[#f2f7fe] h-8 border border-[0.125rem] border-partner rounded-full px-3 py-0.5 flex items-center">
                        <Typography variant="label3" className="!text-partner uppercase">
                          {level}
                        </Typography>
                      </div>
                    ) : (
                      // Future level - gray circle with number
                      <ResponsiveTooltip content={t('dashboard.levelName', { level: level.charAt(0).toUpperCase() + level.slice(1) })} placement="top">
                        <div className="bg-[#eeeeef] rounded-full w-8 h-8 flex items-center justify-center">
                          <span className="text-xs text-neutral-600 uppercase">{index + 1}</span>
                        </div>
                      </ResponsiveTooltip>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Subscribers */}
            <div className="px-4 py-4 h-[5.5rem] flex flex-col gap-2">
              <Typography variant="body2" className="!text-auth-muted !text-sm !font-medium">
                {t('dashboard.subscribers')}
              </Typography>
              <div className="flex gap-2 items-end">
                <Typography variant="h5" className="!text-neutral-slate !text-xl !font-semibold">
                  {totalMonthSubscribers}
                </Typography>
                <Typography variant="body2" className="!text-auth-muted !text-sm !font-normal">
                  / {nextLevelInfo.targetSubscribers}
                </Typography>
              </div>
            </div>
          </div>
        </div>

        {/* Partner Info Card */}
        <div className="flex flex-col gap-4">
          <div className="bg-white border border-gray-90 rounded-3.5 flex flex-col shadow-[0_4px_16px_0_rgba(0,0,0,0.03)]">
            {/* Avatar & Info */}
            <div className="px-4 py-3 flex items-center gap-2 border-b border-gray-90">
              <div className="min-w-[3.125rem] min-h-[3.125rem] max-w-[3.125rem] max-h-[3.125rem] overflow-hidden">
                <Avatar
                  name={managerName}
                  src={AvatarTest}
                  alt={managerAvatarAlt}
                  className="w-full h-full rounded-2 object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <Typography variant="h5" className="!font-medium !text-xl">
                    {managerName}
                  </Typography>
                  <div className="bg-[#CFD8DC] h-6 rounded-md p-1 flex items-center gap-1">
                    <IcTgBlue />
                    <a href="https://t.me/onlyu_partners" target="_blank" rel="noopener noreferrer">
                      <span className="text-sm text-partner truncate">onlyu.partner</span>
                    </a>
                  </div>
                </div>
                <button className="text-sm text-partner hover:underline">
                  {t('dashboard.managerNotResponding')}
                </button>
              </div>
            </div>

            <div className="flex flex-col divide-y divide-gray-90">
              <SimpleStatCard
                className="pb-5"
                label={t('dashboard.workTime')}
                value={t('partner.workTimeExample')}
              />
              {/* <SimpleStatCard className='pb-5' label={t('dashboard.languages')} value={t('partner.languagesExample')} /> */}
              <div className="px-4 py-4 h-[5.5rem] flex flex-col gap-2">
                <Typography variant="body2" className="!text-auth-muted !text-sm !font-medium">
                  {t('dashboard.languages')}
                </Typography>
                <div className="flex gap-2">
                  {['English', 'Русский', 'Українська'].map((lang) => (
                    <div className="bg-auth-surface rounded-1.5 px-2.5 pt-0.5 h-[1.875rem]">
                      <Typography
                        key={lang}
                        variant="h5"
                        className="!text-neutral-slate !text-sm !font-medium"
                      >
                        {lang}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isMobile && (
          <div className="md:mb-2">
            <PartnerNewsBlock />
          </div>
        )}
      </div>

      {/* Statistics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Income Section */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between md:justify-normal gap-4 md:h-6">
            <Typography variant="h5" className="!font-medium !text-xl">
              {t('dashboard.income')}
            </Typography>
            <Link
              to="/partner/account/wallet"
              className="text-partner text-sm font-normal h-5 px-0 min-w-0 hover:underline"
            >
              {t('dashboard.wallet')}
            </Link>
          </div>

          <div className="bg-zinc-100 rounded-xl">
            <Tabs
              selectedKey={activeIncomeTab}
              onSelectionChange={(key) => setActiveIncomeTab(key as string)}
              variant="light"
              fullWidth
              classNames={{
                tabList: 'gap-1 w-full',
                cursor: 'bg-white rounded-lg shadow-sm',
                tab: 'px-3 py-0 text-sm font-normal data-[selected=true]:text-black text-auth-muted',
                tabContent: 'group-data-[selected=true]:text-black',
              }}
            >
              <Tab key="all" title={t('dashboard.allTime')} />
              <Tab key="CPL" title="CPL" />
              <Tab key="RefShare" title="RevShare" />
              <Tab key="Subscription" title="Subscription" />
            </Tabs>
          </div>

          <div className="bg-white border border-gray-90 rounded-3.5 flex flex-col shadow-[0_4px_16px_0_rgba(0,0,0,0.03)]">
            <div className="flex flex-col divide-y divide-gray-90">
              <SimpleStatCard
                label={t('dashboard.receivedAllTime')}
                value={`$${formatMoney(incomeData.all_time)}`}
              />
              <SimpleStatCard
                label={t('dashboard.received30Days')}
                value={`$${formatMoney(incomeData.week_ago)}`}
              />
              {!isCplIncomeTab && (
                <SimpleStatCard
                  label={t('dashboard.pendingAllTime')}
                  value={`$${formatMoney(incomeData.month_ago)}`}
                />
              )}
            </div>
          </div>
        </div>

        {/* Fans Section */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between md:justify-normal gap-4 md:h-6">
            <Typography variant="h5" className="!font-medium !text-xl">
              {t('dashboard.fans')}
            </Typography>
            <Link
              to="/partner/subscribers/sales"
              className="text-partner text-sm font-normal h-5 px-0 min-w-0 hover:underline"
            >
              {t('dashboard.allFans')}
            </Link>
          </div>

          <div className="bg-zinc-100 rounded-xl">
            <Tabs
              selectedKey={activeFansTab}
              onSelectionChange={(key) => setActiveFansTab(key as string)}
              variant="light"
              fullWidth
              classNames={{
                tabList: 'gap-1 w-full',
                cursor: 'bg-white rounded-lg shadow-sm',
                tab: 'px-3 py-0 text-sm font-normal data-[selected=true]:text-black text-auth-muted',
                tabContent: 'group-data-[selected=true]:text-black',
              }}
            >
              <Tab key="all" title={t('dashboard.allTime')} />
              <Tab key="CPL" title="CPL" />
              <Tab key="RefShare" title="RevShare" />
              <Tab key="Subscription" title="Subscription" />
            </Tabs>
          </div>

          <div className="bg-white border border-gray-90 rounded-3.5 flex flex-col shadow-[0_4px_16px_0_rgba(0,0,0,0.03)]">
            <div className="flex flex-col divide-y divide-gray-90">
              <SimpleStatCard
                label={t('dashboard.totalFansAllTime')}
                value={`+${subscribersData.all_time ?? 0}`}
              />
              <SimpleStatCard
                label={t('dashboard.totalFans7Days')}
                value={`+${subscribersData.week_ago ?? 0}`}
              />
              <SimpleStatCard
                label={t('dashboard.totalFans30Days')}
                value={`+${subscribersData.month_ago ?? 0}`}
              />
            </div>
          </div>
        </div>

        {/* Conversion Section */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between md:justify-normal gap-4 md:h-6">
            <Typography variant="h5" className="!font-medium !text-xl">
              {t('dashboard.conversion')}
            </Typography>
            <Link
              to="/partner/statistics/campaigns"
              className="text-partner text-sm font-normal h-5 px-0 min-w-0 hover:underline"
            >
              {t('dashboard.statistics')}
            </Link>
          </div>

          <div className="bg-zinc-100 rounded-xl">
            <Tabs
              selectedKey={activeConversionTab}
              onSelectionChange={(key) => setActiveConversionTab(key as string)}
              variant="light"
              fullWidth
              classNames={{
                tabList: 'gap-1 w-full',
                cursor: 'bg-white rounded-lg shadow-sm',
                tab: 'px-3 py-0 text-sm font-normal data-[selected=true]:text-black text-auth-muted',
                tabContent: 'group-data-[selected=true]:text-black',
              }}
            >
              <Tab key="all" title={t('dashboard.allTime')} />
              <Tab key="CPL" title="CPL" />
              <Tab key="RefShare" title="RevShare" />
              <Tab key="Subscription" title="Subscription" />
            </Tabs>
          </div>

          <div className="bg-white border border-gray-90 rounded-3.5 flex flex-col shadow-[0_4px_16px_0_rgba(0,0,0,0.03)]">
            <div className="flex flex-col divide-y divide-gray-90">
              <SimpleStatCard
                label={t('dashboard.threeMessages')}
                value={(conversionsData?.gte_3_messages_count ?? 0).toString()}
              />
              <SimpleStatCard
                label={t('dashboard.onePurchase')}
                value={(conversionsData?.sales ?? 0).toString()}
              />
              <SimpleStatCard
                label={t('dashboard.arpu')}
                value={`$${formatMoney(conversionsData.arpu)}`}
              />
            </div>
          </div>
        </div>
      </div>

      {isMobile && (
        <div className="mt-4">
          <PartnerNewsBlock />
        </div>
      )}
    </div>
  );
};

export default ApprovedPartnerDashboard;
